import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Share, MoreVertical, X } from 'lucide-react';
import {
  WELCOME_TOUR_DONE_EVENT,
  detectInstallPlatform,
  getAppDisplayName,
  getAppShortName,
  getSiteHost,
  isA2hsDismissed,
  isStandaloneDisplay,
  markA2hsDismissed,
} from './addToHomeScreenUtils';
import { isWelcomeTourDone } from './WorkspaceWelcomeTour';
import { useWorkspace } from '../WorkspaceContext';

/**
 * Mobile "Add to Home Screen" bottom sheet.
 * Shows after the welcome tour finishes/skips (or immediately if tour already done).
 * iOS → Safari Share instructions; Android → Chrome menu / native install prompt.
 */
const AddToHomeScreenSheet = () => {
  const { business } = useWorkspace();
  const [open, setOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const shownRef = useRef(false);

  const platformInfo = useMemo(() => detectInstallPlatform(), []);
  const host = useMemo(() => getSiteHost(), []);
  const appName = getAppDisplayName();
  const shortName = getAppShortName();

  const tryOpen = useCallback(() => {
    if (shownRef.current) return;
    if (isStandaloneDisplay()) return;
    if (isA2hsDismissed()) return;
    if (!platformInfo.isMobile) return;
    if (!(platformInfo.isIOS || platformInfo.isAndroid)) return;

    shownRef.current = true;
    setOpen(true);
  }, [platformInfo]);

  // Capture Chrome/Android install prompt when available.
  useEffect(() => {
    const onBeforeInstall = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  // After welcome tour — or shortly after load if tour already completed.
  useEffect(() => {
    const onTourDone = () => {
      window.setTimeout(tryOpen, 450);
    };

    window.addEventListener(WELCOME_TOUR_DONE_EVENT, onTourDone);

    let timer;
    if (business?.onboardingComplete && isWelcomeTourDone(business?.id)) {
      timer = window.setTimeout(tryOpen, 900);
    }

    return () => {
      window.removeEventListener(WELCOME_TOUR_DONE_EVENT, onTourDone);
      if (timer) window.clearTimeout(timer);
    };
  }, [business?.id, business?.onboardingComplete, tryOpen]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') dismiss();
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const dismiss = () => {
    markA2hsDismissed();
    setOpen(false);
  };

  const handleNativeInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch {
      // user dismissed prompt
    }
    setDeferredPrompt(null);
    markA2hsDismissed();
    setOpen(false);
  };

  if (!open) return null;

  const isIOS = platformInfo.isIOS;
  const instructionLabel = isIOS ? 'Safari instructions' : 'Chrome instructions';

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center sm:hidden"
      role="dialog"
      aria-modal="true"
      aria-labelledby="a2hs-title"
    >
      <button
        type="button"
        aria-label="Dismiss"
        className="absolute inset-0 bg-ink-950/50"
        onClick={dismiss}
      />

      <div className="tour-sheet relative z-[71] w-full max-w-lg rounded-t-[1.75rem] bg-sand-50 px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-3 shadow-[0_-20px_50px_-30px_rgba(6,18,15,0.45)] ring-1 ring-ink-900/10">
        <div className="mx-auto h-1 w-10 rounded-full bg-ink-900/15" aria-hidden="true" />

        <button
          type="button"
          onClick={dismiss}
          aria-label="Close"
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-ink-900/5 text-ink-600 transition-colors hover:bg-ink-900/10 hover:text-ink-900"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="mt-5 flex flex-col items-center text-center">
          <div className="h-16 w-16 overflow-hidden rounded-[1.15rem] bg-ink-900 ring-1 ring-ink-900/10">
            <img
              src="/logo192.png"
              alt=""
              className="h-full w-full scale-110 object-cover"
            />
          </div>
          <p className="mt-2 text-xs text-ink-600/55">{host}</p>
          <h2
            id="a2hs-title"
            className="mt-3 font-display text-[1.65rem] font-light leading-tight tracking-tight text-ink-900"
          >
            Install {shortName}
          </h2>
          <p className="mt-2 max-w-xs text-sm leading-relaxed text-ink-700/75">
            Get instant access and the full app experience — right from your home screen.
          </p>
        </div>

        {!isIOS && deferredPrompt ? (
          <button
            type="button"
            onClick={handleNativeInstall}
            className="mt-6 w-full bg-saffron-500 px-5 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400"
          >
            Add to Home Screen
          </button>
        ) : (
          <div className="mt-6 overflow-hidden rounded-2xl border border-ink-900/10 bg-white">
            <p className="px-4 pt-4 text-center text-[11px] font-bold uppercase tracking-[0.16em] text-saffron-600">
              {instructionLabel}
            </p>

            <ul className="mt-2 divide-y divide-ink-900/10">
              {isIOS ? (
                <>
                  <li className="flex items-start gap-3 px-4 py-3.5 text-left">
                    <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-saffron-500/15 text-saffron-700">
                      <Share className="h-5 w-5" strokeWidth={2.25} />
                    </span>
                    <p className="pt-1.5 text-sm leading-snug text-ink-800">
                      Tap the <span className="font-semibold text-saffron-600">Share</span> button
                      at the bottom of your screen.
                    </p>
                  </li>
                  <li className="flex items-start gap-3 px-4 py-3.5 text-left">
                    <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-saffron-500/15 text-saffron-700">
                      <Plus className="h-5 w-5" strokeWidth={2.25} />
                    </span>
                    <p className="pt-1.5 text-sm leading-snug text-ink-800">
                      Choose{' '}
                      <span className="font-semibold text-saffron-600">Add to Home Screen</span>.
                    </p>
                  </li>
                </>
              ) : (
                <>
                  <li className="flex items-start gap-3 px-4 py-3.5 text-left">
                    <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-saffron-500/15 text-saffron-700">
                      <MoreVertical className="h-5 w-5" strokeWidth={2.25} />
                    </span>
                    <p className="pt-1.5 text-sm leading-snug text-ink-800">
                      Tap the <span className="font-semibold text-saffron-600">menu</span> (⋮) in
                      the top-right corner.
                    </p>
                  </li>
                  <li className="flex items-start gap-3 px-4 py-3.5 text-left">
                    <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-saffron-500/15 text-saffron-700">
                      <Plus className="h-5 w-5" strokeWidth={2.25} />
                    </span>
                    <p className="pt-1.5 text-sm leading-snug text-ink-800">
                      Choose{' '}
                      <span className="font-semibold text-saffron-600">Install app</span> or{' '}
                      <span className="font-semibold text-saffron-600">Add to Home screen</span>.
                    </p>
                  </li>
                </>
              )}
            </ul>
          </div>
        )}

        <p className="mt-4 text-center text-[11px] leading-relaxed text-ink-500/70">
          Saves as <span className="font-medium text-ink-700">{appName}</span>
        </p>

        <button
          type="button"
          onClick={dismiss}
          className="mt-3 w-full rounded-full border border-ink-900/10 bg-white px-5 py-3.5 text-sm font-semibold text-ink-700 transition-colors hover:bg-sand-100"
        >
          Not now
        </button>
      </div>
    </div>
  );
};

export default AddToHomeScreenSheet;
